import { useState, useCallback } from 'react';
import { validateEmail } from '../../../../../utils/accountValidation';
import { debounce } from '../../../../../utils/debounce';

interface UseAuthValidationProps {
  checkUsernameAvailability: (username: string) => Promise<boolean>;
  checkEmailAvailability: (email: string) => Promise<boolean>;
}

interface UseAuthValidationReturn {
  // Username validation
  usernameAvailable: boolean | null;
  checkingUsername: boolean;
  setUsernameAvailable: (available: boolean | null) => void;
  checkUsername: (username: string) => void;

  // Email validation
  emailValid: boolean | null;
  emailAvailable: boolean | null;
  checkingEmail: boolean;
  setEmailValid: (valid: boolean | null) => void;
  setEmailAvailable: (available: boolean | null) => void;
  checkEmail: (email: string) => void;
  resetEmailValidation: () => void;
}

export const useAuthValidation = ({
  checkUsernameAvailability,
  checkEmailAvailability,
}: UseAuthValidationProps): UseAuthValidationReturn => {
  // Username validation state
  const [usernameAvailable, setUsernameAvailable] = useState<boolean | null>(null);
  const [checkingUsername, setCheckingUsername] = useState(false);

  // Email validation state
  const [emailValid, setEmailValid] = useState<boolean | null>(null);
  const [emailAvailable, setEmailAvailable] = useState<boolean | null>(null);
  const [checkingEmail, setCheckingEmail] = useState(false);

  // Debounced username availability check
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const checkUsername = useCallback(
    debounce(async (username: string) => {
      if (!username || username.length < 3) {
        setUsernameAvailable(null);
        return;
      }

      setCheckingUsername(true);
      const available = await checkUsernameAvailability(username);
      setUsernameAvailable(available);
      setCheckingUsername(false);
    }, 500),
    [checkUsernameAvailability]
  );

  // Debounced email validation and availability check
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const checkEmail = useCallback(
    debounce(async (email: string) => {
      if (!email) {
        setEmailValid(null);
        setEmailAvailable(null);
        return;
      }

      // First check email format
      const validation = validateEmail(email);
      setEmailValid(validation.isValid);

      if (!validation.isValid) {
        setEmailAvailable(null);
        return;
      }

      // If format is valid, check availability
      setCheckingEmail(true);
      const available = await checkEmailAvailability(email);
      setEmailAvailable(available);
      setCheckingEmail(false);
    }, 500),
    [checkEmailAvailability]
  );

  const resetEmailValidation = useCallback(() => {
    setEmailValid(null);
    setEmailAvailable(null);
    setCheckingEmail(false);
  }, []);

  return {
    usernameAvailable,
    checkingUsername,
    setUsernameAvailable,
    checkUsername,
    emailValid,
    emailAvailable,
    checkingEmail,
    setEmailValid,
    setEmailAvailable,
    checkEmail,
    resetEmailValidation,
  };
};
